///
/// EarthBrowser
///
/// Created by Matt Giger on 9/6/2026
/// Copyright (c) 2026 Geodata Labs
///

import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { cSurfaceOverlayDepthBias } from "@/earthbrowser/entities/surfaceDepthBias"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type color } from "@stovokor/math/color"
import { dot, type vec3 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"
import type { WatchWarningSegment } from "./HurricanesTable"
import { slerpUnit, surfacePoint, unitNormal } from "./stormTrack"

const cLineWidth = 4
const cLineWidthFocused = 5.5

// Coastline paths arrive dense, but offshore watch lines between islands can
// have multi-degree gaps that would chord under the globe
const cMaxStepRad = 0.006

/**
 * Coastal watch/warning highlights for one storm: thick polylines along the
 * affected coastline in the standard NHC palette (red hurricane warning, pink
 * hurricane watch, blue tropical storm warning, yellow tropical storm watch).
 * Warnings render above watches so an upgraded stretch of coast shows red.
 */
export class StormWatchWarningNode extends NodeBase {
	private readonly lines: PolylineNode[] = []
	private focused = false

	constructor(context: StovokorContext, segments: WatchWarningSegment[], altitudeKm: number) {
		super()

		// Watches first, warnings last (later nodes draw over earlier ones)
		const ordered = [...segments].sort((a, b) => severity(a.code) - severity(b.code))

		for (const segment of ordered) {
			const segmentColor = watchWarningColor(segment.code)
			if (!segmentColor) continue

			for (const path of segment.paths) {
				const positions = densifyPath(path, altitudeKm)
				if (positions.length < 2) continue
				const line = new PolylineNode(context, {
					positions,
					width: cLineWidth,
					color: segmentColor,
					depthWrite: false,
					polygonOffset: cSurfaceOverlayDepthBias,
				})
				this.lines.push(line)
				this.addNode(line)
			}
		}
	}

	/** Thicken the coastal highlights while the storm is selected */
	public setFocused(focused: boolean): void {
		if (this.focused === focused) return
		this.focused = focused
		const width = focused ? cLineWidthFocused : cLineWidth
		for (const line of this.lines) {
			line.updateWidth(width)
		}
	}
}

/** NHC display color for a TCWW code, null for unknown codes */
export function watchWarningColor(code: string): color | null {
	switch (code.toUpperCase()) {
		case "HWR": return DesignColors.STORM_WW_HURRICANE_WARNING
		case "HWA": return DesignColors.STORM_WW_HURRICANE_WATCH
		case "TWR": return DesignColors.STORM_WW_TS_WARNING
		case "TWA": return DesignColors.STORM_WW_TS_WATCH
		default: return null
	}
}

/** Human-readable name for a TCWW code */
export function watchWarningLabel(code: string): string {
	switch (code.toUpperCase()) {
		case "HWR": return "Hurricane Warning"
		case "HWA": return "Hurricane Watch"
		case "TWR": return "Tropical Storm Warning"
		case "TWA": return "Tropical Storm Watch"
		default: return code
	}
}

function severity(code: string): number {
	switch (code.toUpperCase()) {
		case "TWA": return 0
		case "TWR": return 1
		case "HWA": return 2
		case "HWR": return 3
		default: return -1
	}
}

/** Convert a [lon, lat] path to world positions, subdividing long gaps */
function densifyPath(path: [number, number][], altitudeKm: number): vec3[] {
	if (path.length === 0) return []

	const positions: vec3[] = []
	let prev = unitNormal(path[0][1], path[0][0])
	positions.push(surfacePoint(prev, altitudeKm))

	for (let i = 1; i < path.length; i++) {
		const next = unitNormal(path[i][1], path[i][0])
		const angle = Math.acos(Math.max(-1, Math.min(1, dot(prev, next))))
		const steps = Math.max(1, Math.ceil(angle / cMaxStepRad))
		for (let s = 1; s <= steps; s++) {
			positions.push(surfacePoint(slerpUnit(prev, next, s / steps), altitudeKm))
		}
		prev = next
	}
	return positions
}
